import { THREE } from "@scrolly/renderer";
import { deckleTexturFuerMobil } from "../../utils/geraet";
import { WebGLAssets } from "../webglAssets";

export const AssetLibraryType = {
    texture: 0,
    textureCube: 1,
} as const;
export type AssetLibraryType = (typeof AssetLibraryType)[keyof typeof AssetLibraryType];

export const AssetLibraryCategory = {
    roughness: 0,
    matcap: 1,
    skybox: 2,
    texture: 3,
} as const;
export type AssetLibraryCategory = (typeof AssetLibraryCategory)[keyof typeof AssetLibraryCategory];

interface AssetQueued {
    id: string;
    type: AssetLibraryType;
    // Legacy code typing
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    content: any;
    category: AssetLibraryCategory;
}

class AssetLibrary {
    assets: Array<{
        texture: THREE.Texture;
        id: string;
        loaded: boolean;
        category: AssetLibraryCategory;
    }> = [];
    queue: Array<AssetQueued> = [];
    allLoaded = false;

    getAsset(id: string) {
        const asset = this.assets.find((m) => m.id === id);
        return asset?.texture;
    }

    queueAsset(asset: AssetQueued) {
        const { id, category, type, content } = asset;
        this.queue.push({
            id,
            type,
            category,
            content,
        });
    }

    loadAsset(asset: AssetQueued, callback?: TODOAny) {
        const { id, category, type, content } = asset;
        switch (type) {
            case AssetLibraryType.texture:
                return this.loadTexture2D(content, (texture) => {
                    if (callback) {
                        callback(texture);
                    }
                    this.pushTexture(category, id, texture);
                });
            case AssetLibraryType.textureCube:
                return this.loadTextureCube(content, (texture) => {
                    if (callback) {
                        callback(texture);
                    }
                    this.pushTexture(category, id, texture);
                });
            default:
                return undefined;
        }
    }

    loadAssets() {
        const promises = this.queue.map(
            (asset) =>
                new Promise((resolve) => {
                    this.loadAsset(asset, resolve);
                })
        );
        this.queue = [];
        return Promise.all(promises).then(() => {
            this.allLoaded = true;
        });
    }

    pushTexture(category: AssetLibraryCategory, id: string, texture: THREE.Texture) {
        this.assets.push({
            id,
            texture,
            category,
            loaded: true,
        });
    }

    loadTexture2D(image: TODOAny, callback: (texture: THREE.Texture) => void): THREE.Texture {
        const finish = (texture: THREE.Texture) => {
            // Mobile-Textur-Deckel (26.08.2026): 3D-Material-Maps kommen
            // verlustfrei vom Backend (bewusst, s. AssetDropZone) — auf
            // Touch-Geraeten vor dem GPU-Upload auf 2048 begrenzen.
            deckleTexturFuerMobil(texture);
            texture.wrapS = THREE.RepeatWrapping;
            texture.wrapT = THREE.RepeatWrapping;
            texture.minFilter = THREE.LinearMipMapLinearFilter;
            texture.magFilter = THREE.LinearFilter;
            callback(texture);
        };
        // Editor-lokale Texturen ("local-*:<id>", IndexedDB) brauchen erst eine
        // async Aufloesung (Jans Original, via WebGLAssets-Hook statt Direkt-
        // import — Paketgrenze). Leere Textur SYNCHRON zurueckgeben, damit die
        // Material-Zuweisung greift, und nach der Aufloesung befuellen. Ohne
        // diesen Zweig blieb jede lokal gespeicherte 3D-Textur schwarz (Jans
        // Plane-Fund). Reader: Hook null, veroeffentlichte URLs laufen direkt.
        if (typeof image === "string" && image.startsWith("local-") && WebGLAssets.resolveLocalMediaUrl) {
            const texture = new THREE.Texture();
            WebGLAssets.resolveLocalMediaUrl(image).then((url) => {
                if (!url) {
                    callback(texture);
                    return;
                }
                new THREE.TextureLoader().load(
                    url,
                    (loaded) => {
                        texture.image = loaded.image;
                        texture.needsUpdate = true;
                        finish(texture);
                    },
                    undefined,
                    () => callback(texture)
                );
            });
            return texture;
        }
        const loader = new THREE.TextureLoader();
        // Fehler-Callback ZWINGEND: loadAssets() wartet per Promise auf den
        // Callback. Ohne onError liess EINE 404-Textur (z. B. fehlende
        // PBR-Preset-Textur im Viewer) das Promise ewig haengen — und damit
        // jeden updateMaterial-Await: das ganze 3D-Objekt erschien nie
        // (Jans Komplex-Scrolly 01.08.2026, Rietscheldenkmal). Bei Fehler mit
        // der leeren Textur aufloesen: Objekt kommt, nur ohne diese Map.
        return loader.load(
            image,
            (texture) => finish(texture),
            undefined,
            () => {
                console.warn(`AssetLibrary: Textur '${image}' konnte nicht geladen werden`);
                callback(new THREE.Texture());
            }
        );
    }

    loadTextureCube(images: TODOAny, callback: (texture: THREE.Texture) => void): THREE.CubeTexture {
        const loader = new THREE.CubeTextureLoader();
        // Gleiches Haenger-Risiko wie bei loadTexture2D — bei Fehler aufloesen.
        return loader.load(images, callback, undefined, () => {
            console.warn(`AssetLibrary: CubeMap konnte nicht geladen werden`);
            callback(new THREE.CubeTexture());
        });
    }
}

export default AssetLibrary;
